import {
  CalculateCartPromotion,
  CalculateCartResult,
} from "../types/CalculateCart";

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const toArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const toNumber = (value: unknown): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const parsed = Number(value.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const toOptionalNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const firstNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    const parsed = toOptionalNumber(value);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

/**
 * Сколько баллов можно списать в этом заказе — `discountAmountForCurrentOrder`.
 * Это итог Mindbox: лимит от чека (сейчас 20%) и остаток баланса уже учтены,
 * сами мы ничего не пересчитываем. Поле ищем там, где его кладёт бэкенд, и в
 * блоках Mindbox; старое имя `availableAmountForCurrentOrder` — запасной
 * вариант для прежних ответов.
 */
const parseAvailablePoints = (
  root: Record<string, unknown>,
  cart: Record<string, unknown>,
  order: Record<string, unknown>,
): {
  availableAmountForCurrentOrder: number;
  availablePoints: number;
} => {
  const totalBonusPointsInfo = toRecord(order.totalBonusPointsInfo);
  const perBalance = toArray(order.bonusPointsInfoPerBalanceType).map(toRecord);
  const sources = [root, cart, order, totalBonusPointsInfo];

  const pick = (field: string) =>
    firstNumber(...sources.map((source) => source[field])) ??
    perBalance.reduce<number | null>((max, item) => {
      const value = toOptionalNumber(item[field]);
      return value === null ? max : Math.max(max ?? 0, value);
    }, null);

  const amount =
    pick("discountAmountForCurrentOrder") ??
    pick("availableAmountForCurrentOrder") ??
    0;

  return {
    availableAmountForCurrentOrder: Math.max(0, amount),
    availablePoints: pick("availablePointsForCurrentOrder") ?? amount,
  };
};

const parseCouponStatus = (order: Record<string, unknown>) => {
  const statuses = toArray(order.couponsInfo)
    .map((item) => {
      const record = toRecord(item);
      const coupon = toRecord(record.coupon);
      return String(coupon.status ?? record.status ?? "").trim();
    })
    .filter(Boolean);

  const couponStatus = statuses[0] ?? null;
  const isPromoValid = couponStatus ? couponStatus === "CanBeUsed" : null;

  return { couponStatus, isPromoValid };
};

export const getCalculateCartCouponStatus = (response: unknown) => {
  const root = toRecord(response);
  const cart = toRecord(root.cart);
  const order = toRecord(cart.order);

  return parseCouponStatus(order).couponStatus;
};

const parsePromotions = (
  cart: Record<string, unknown>,
  root: Record<string, unknown>,
): CalculateCartPromotion[] =>
  toArray(cart.appliedPromotions ?? root.appliedPromotions)
    .map((item) => {
      const record = toRecord(item);
      const promotion = toRecord(record.promotion);

      return {
        name: String(promotion.name ?? record.name ?? "").trim(),
        amount: toNumber(record.amount),
      };
    })
    .filter((item) => item.name || item.amount);

export const parseCalculateCartResult = (
  response: unknown,
  promoCode = "",
): CalculateCartResult | null => {
  const root = toRecord(response);
  const cart = toRecord(root.cart);
  const order = toRecord(cart.order);

  if (root.success !== true || cart.status !== "Success") {
    return null;
  }

  const { availableAmountForCurrentOrder, availablePoints } =
    parseAvailablePoints(root, cart, order);
  const { couponStatus, isPromoValid } = parseCouponStatus(order);
  const bonusChanges = toArray(order.bonusPointsChanges);
  const firstBonusChange = toRecord(bonusChanges[0]);
  const appliedPromotions = parsePromotions(cart, root);
  const subtotalPrice = firstNumber(
    order.orderSum,
    order.sum,
    order.productsSum,
    order.subtotal,
    cart.sum,
  );
  const deliveryPrice = firstNumber(
    order.deliveryCost,
    order.deliveryPrice,
    order.delivery,
    cart.deliveryCost,
  );
  const totalPrice = firstNumber(
    order.totalPrice,
    order.total,
    order.totalSum,
    order.priceForPay,
    order.toPay,
    cart.totalPrice,
    cart.totalSum,
  );
  const hasDiscount =
    appliedPromotions.length > 0 ||
    (subtotalPrice !== null &&
      deliveryPrice !== null &&
      totalPrice !== null &&
      subtotalPrice + deliveryPrice > totalPrice);
  const resolvedPromoValidity = promoCode
    ? isPromoValid || hasDiscount
    : null;

  return {
    availablePoints,
    availableAmountForCurrentOrder,
    // Лимит списания задаёт Mindbox, бэкенд отдаёт его для подсказки под
    // тумблером. Где именно лежит ключ — в корне или в cart — не завязываемся.
    bonusPaymentPercent: firstNumber(
      root.mindbox_bonus_payment_percent,
      cart.mindbox_bonus_payment_percent,
      order.mindbox_bonus_payment_percent,
    ),
    earnedAmount: toNumber(firstBonusChange.earnedAmount),
    appliedPromotions,
    promoCode,
    isPromoValid: resolvedPromoValidity,
    couponStatus,
    subtotalPrice,
    deliveryPrice,
    totalPrice,
  };
};
